import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import logo from "../assets/logo-light.png";

const navLink = ({ isActive }) =>
  `text-sm tracking-wider-2 uppercase font-body font-medium transition-colors ${
    isActive ? "text-sage" : "text-darkRoast hover:text-sage"
  }`;

export default function Nav() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-cream/85 border-b border-darkRoast/10"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
          <img
            src={logo}
            alt="Rooted in Amador, Natural Seed Shop logo"
            className="w-14 h-14 object-contain transition-transform group-hover:scale-105"
            data-testid="nav-logo-img"
          />
          <div className="leading-tight">
            <div className="font-display text-xl text-darkRoast tracking-tight">Rooted in Amador</div>
            <div className="text-[10px] tracking-wider-2 uppercase text-earth">Natural Seed Shop</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/" end className={navLink} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop" className={navLink} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/grow" className={navLink} data-testid="nav-grow">How to Grow</NavLink>
          <NavLink to="/about" className={navLink} data-testid="nav-about">Our Story</NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={navLink} data-testid="nav-admin">Admin</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link to="/profile" className="hidden sm:flex items-center gap-2 text-sm text-darkRoast hover:text-sage" data-testid="nav-profile">
                <User className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden lg:inline">{user.name?.split(" ")[0]}</span>
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-earth hover:text-darkRoast" data-testid="nav-logout">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <Link to="/login" className="text-sm tracking-wider-2 uppercase text-darkRoast hover:text-sage" data-testid="nav-login">
              Sign in
            </Link>
          )}
          <Link
            to="/cart"
            className="relative w-10 h-10 rounded-full bg-darkRoast text-cream flex items-center justify-center hover:bg-sage transition-colors"
            data-testid="nav-cart"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-terracotta text-cream text-[10px] w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}
